import { getApiConfig } from '../utils/apiUtils';
import axios from 'axios';
import moment from 'moment';

const API_BASE = '/distribution/api/inventory-blocks';

/** Default block start (after typical checkout) / unblock end-of-day. */
export const defaultInventoryBlockStartTime = () => moment('12:00', 'HH:mm');
export const defaultInventoryBlockEndTime = () => moment('23:59', 'HH:mm');

export function parseInventoryBlockTime(value) {
  if (value == null || value === '') return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  const parsed = moment(String(value).trim(), ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'], true);
  return parsed.isValid() ? parsed : null;
}

/** Format form/API time for create/update payload (`HH:mm`). */
export function formatInventoryBlockTime(value) {
  const parsed = parseInventoryBlockTime(value);
  return parsed ? parsed.format('HH:mm') : null;
}

export const fetchAllInventoryBlocks = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.room_type_id) params.append('room_type_id', filters.room_type_id);
  if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
  if (filters.active_today !== undefined) params.append('active_today', filters.active_today);
  
  const url = params.toString() ? `${API_BASE}/?${params.toString()}` : `${API_BASE}/`;
  const response = await axios.get(url, getApiConfig());
  return response.data;
};

/** Manage Event: blocks whose half-open range covers hotel-local today (includes inactive). */
export const fetchActiveTodayInventoryBlocks = async (filters = {}) => {
  return fetchAllInventoryBlocks({ ...filters, active_today: true });
};

export const fetchInventoryBlockById = async (blockId) => {
  const response = await axios.get(`${API_BASE}/${blockId}/`, getApiConfig());
  return response.data;
};

export const createInventoryBlock = async (blockData) => {
  const response = await axios.post(`${API_BASE}/`, blockData, getApiConfig());
  return response.data;
};

export const updateInventoryBlock = async (blockId, blockData) => {
  const response = await axios.put(`${API_BASE}/${blockId}/`, blockData, getApiConfig());
  return response.data;
};

/**
 * Enable/disable a block via update (triggers full inventory recalculation).
 * Does not resubmit room_ids/channel_ids — M2M stays as-is — so disable works
 * even after blocked rooms were later booked. Inventory is recalculated from is_active.
 */
export const setInventoryBlockActive = async (block, isActive) => {
  if (!block?.id) {
    throw new Error('Inventory block id is required');
  }
  return updateInventoryBlock(block.id, {
    room_type_id: block.room_type_id,
    block_type: block.block_type,
    start_date: block.start_date,
    end_date: block.end_date,
    start_time: block.start_time || null,
    end_time: block.end_time || null,
    num_rooms: block.num_rooms || 1,
    reason: block.reason || null,
    is_active: Boolean(isActive),
    // Must send include_pms explicitly — serializer default False would clear it if omitted
    include_pms: Boolean(block.include_pms),
  });
};

/** Prefer API error / first validation message for toggle UX. */
export const getInventoryBlockErrorMessage = (error, fallback = 'Failed to update inventory block') => {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;
  if (typeof data.error === 'string' && data.error) return data.error;
  if (data.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return fallback;
};

export const deleteInventoryBlock = async (blockId) => {
  const response = await axios.delete(`${API_BASE}/${blockId}/`, getApiConfig());
  return response.data;
};

/**
 * Availability for inventory-block room picker:
 * - room_ids: allocated rooms that cannot be selected
 * - capacity_by_room_type: max rooms that can be blocked per type
 *   (accounts for allocated + unallocated bookings)
 */
export const fetchBlockRoomAvailability = async (startDate, endDate, options = {}) => {
  const params = new URLSearchParams({
    booked_room_ids: '1',
    start_date: startDate,
    end_date: endDate,
  });
  if (options.excludeBlockId) {
    params.append('exclude_block_id', String(options.excludeBlockId));
  }
  const response = await axios.get(`${API_BASE}/?${params.toString()}`, getApiConfig());
  return {
    room_ids: Array.isArray(response.data?.room_ids)
      ? response.data.room_ids.map((id) => Number(id)).filter(Boolean)
      : [],
    capacity_by_room_type: response.data?.capacity_by_room_type || {},
  };
};

/** @deprecated Prefer fetchBlockRoomAvailability */
export const fetchBookedRoomIdsForRange = async (startDate, endDate) => {
  const data = await fetchBlockRoomAvailability(startDate, endDate);
  return data.room_ids;
};

